// Currently in use
import React from "react";
import {
  Backdrop,
  Box,
  Container,
  Paper,
  InputAdornment,
  TextField,
  IconButton,
  Divider,
  Button,
  CircularProgress,
} from "@mui/material";
import { CloseRounded, SearchOutlined } from "@mui/icons-material";

import CustomTabPanel from "components/Shared/CustomTabPanel";
import SearchResultTabs from "./searchResults/SearchResultTabs";
import { useSidebarLayoutContext } from "contexts/SidebarLayoutContext";
import { useDebounce } from "hooks";
import { useLazyGetSearchResultQuery } from "store/services/searchApi";

import ShipmentResult from "./searchResults/ShipmentResult";
import VendorResult from "./searchResults/VendorResult";
import CustomerResult from "./searchResults/CustomerResult";

const MIN_SEARCH_LENGTH = 3;
const RESULTS_PER_PAGE = 20;

const AdminSearchBar = () => {
  const [showSearch, setShowSearch] = React.useState(false);
  const [keyword, setKeyword] = React.useState("");
  const [searchResults, setSearchResults] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState("All");

  // Track per-category pages for "Load More"
  const [categoryPages, setCategoryPages] = React.useState({
    Shipment: 1,
    Vendor: 1,
    Customer: 1,
  });

  // Track which category is currently loading more
  const [loadingMore, setLoadingMore] = React.useState(null);

  const { setShowSidebar } = useSidebarLayoutContext();

  const { debouncedValue: debouncedKeyword } = useDebounce(keyword, 500);

  // RTK Query hook to fetch search results
  const [
    searchKeyword,
    {
      data,
      isLoading: loadingSearchResults,
      isFetching: fetchingSearchResults,
    },
  ] = useLazyGetSearchResultQuery();

  // Toggle search visibility
  const handleToggleShow = () => {
    setShowSearch((prev) => !prev);
    setShowSidebar(showSearch);
    setKeyword("");
    setSearchResults(null);
    setActiveTab("All");
    setCategoryPages({ Shipment: 1, Vendor: 1, Customer: 1 });
    setLoadingMore(null);
  };

  // Runs on every keystroke (typing OR deleting). Clearing searchResults
  // here immediately - rather than waiting for the debounce/fetch cycle -
  // is what stops old results from staying on screen when the user
  // backspaces below the minimum length or edits the keyword.
  const handleKeywordChange = (e) => {
    const sanitizedKeyword = e.target.value
      .replace(/-/g, "")
      .replace(/[^a-zA-Z0-9\s.]/g, "");
    setKeyword(sanitizedKeyword);
    setSearchResults(null);
    setCategoryPages({ Shipment: 1, Vendor: 1, Customer: 1 });
    setLoadingMore(null);
  };

  const handleClearKeyword = () => {
    setKeyword("");
    setSearchResults(null);
    setCategoryPages({ Shipment: 1, Vendor: 1, Customer: 1 });
    setLoadingMore(null);
  };

  // Load more results for a specific category
  const handleLoadMore = async (category) => {
    const nextPage = categoryPages[category] + 1;
    setLoadingMore(category);

    try {
      const pageParamKey = `${category.toLowerCase()}Page`;
      const result = await searchKeyword({
        keyword: debouncedKeyword,
        limit: RESULTS_PER_PAGE,
        [pageParamKey]: nextPage,
        // Only fetch the specific category to avoid refetching others
        filter: category,
        _t: Date.now(),
      }).unwrap();

      // Append new results to existing ones
      setSearchResults((prev) => {
        if (!prev) return prev;

        const categoryData = result[category];
        if (!categoryData) return prev;

        return {
          ...prev,
          [category]: {
            ...categoryData,
            results: [
              ...(prev[category]?.results || []),
              ...categoryData.results,
            ],
          },
        };
      });

      setCategoryPages((prev) => ({
        ...prev,
        [category]: nextPage,
      }));
    } catch (error) {
      console.error(`Failed to load more ${category} results:`, error);
    } finally {
      setLoadingMore(null);
    }
  };

  // Memoized search result counts for each category (show total counts)
  const searchResultCounts = React.useMemo(() => {
    if (!searchResults) return {};

    return {
      Shipment: searchResults.Shipment?.totalCount || 0,
      Vendor: searchResults.Vendor?.totalCount || 0,
      Customer: searchResults.Customer?.totalCount || 0,
    };
  }, [searchResults]);

  // Calculate the total count of all search results
  const totalResultCount = React.useMemo(() => {
    return Object.values(searchResultCounts).reduce((acc, count) => acc + count, 0);
  }, [searchResultCounts]);

  // Fetch search results only once the debounced keyword has at least
  // MIN_SEARCH_LENGTH characters. Previously this fired on ANY non-empty
  // keyword (even 1 character), hammering the API on every keystroke.
  React.useEffect(() => {
    if (debouncedKeyword && debouncedKeyword.length >= MIN_SEARCH_LENGTH) {
      searchKeyword({
        keyword: debouncedKeyword,
        limit: RESULTS_PER_PAGE,
        _t: Date.now(),
      });
    }
  }, [debouncedKeyword, searchKeyword]);

  // Update search results from API data (only for initial/full searches, not "load more")
  React.useEffect(() => {
    if (!data || loadingMore) return;
    setSearchResults(data);
  }, [data, loadingMore]);

  // Helper to render "Load More" button for a category
  const renderLoadMore = (category) => {
    const categoryData = searchResults?.[category];
    if (!categoryData?.hasMore) return null;

    const loadedCount = categoryData.results?.length || 0;
    const totalCount = categoryData.totalCount || 0;

    return (
      <Box display="flex" justifyContent="center" paddingY={1}>
        <Button
          size="small"
          variant="text"
          disabled={loadingMore === category}
          onClick={(e) => {
            e.stopPropagation();
            handleLoadMore(category);
          }}
          startIcon={
            loadingMore === category ? (
              <CircularProgress size={16} />
            ) : null
          }
          sx={{ textTransform: "none" }}
        >
          {loadingMore === category
            ? "Loading..."
            : `Load More (${loadedCount} of ${totalCount})`}
        </Button>
      </Box>
    );
  };

  return (
    <>
      <Paper
        elevation={0}
        variant="outlined"
        className="max-w-sm flex-grow rounded px-4 py-2 text-gray-500"
        onClick={handleToggleShow}
      >
        Search...
      </Paper>

      <Backdrop
        open={showSearch}
        onClick={handleToggleShow}
        sx={{
          color: "#fff",
          backdropFilter: "blur(10px)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Container
          maxWidth="md"
          style={{ position: "absolute", top: 50 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Paper variant="outlined">
            <Box padding={2}>
              <TextField
                autoFocus
                value={keyword}
                onChange={handleKeywordChange}
                fullWidth
                placeholder={`Type at least ${MIN_SEARCH_LENGTH} characters to search...`}
                size="small"
                loading={loadingSearchResults || (fetchingSearchResults && !loadingMore)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {!!keyword.length && (
                        <IconButton onClick={handleClearKeyword}>
                          <CloseRounded />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Divider />

            <>
              <SearchResultTabs
                tab={activeTab}
                searchResultCounts={searchResultCounts}
                handleTabChange={(_, tab) => setActiveTab(tab)}
              />

              <Box paddingY={2}>
                <Box className="max-h-[450px] overflow-y-scroll" onClick={handleToggleShow}>
                  {searchResults && Object.keys(searchResults).length ? (
                    totalResultCount > 0 ? (
                      <>
                        <CustomTabPanel activeTab={activeTab} value="All">
                          <Box display="flex" flexDirection="column" gap={1}>
                            {!!searchResults["Shipment"]?.results?.length && (
                              <>
                                <ShipmentResult shipments={searchResults["Shipment"].results} />
                                {renderLoadMore("Shipment")}
                              </>
                            )}
                            {!!searchResults["Vendor"]?.results?.length && (
                              <>
                                <VendorResult vendors={searchResults["Vendor"].results} />
                                {renderLoadMore("Vendor")}
                              </>
                            )}
                            {!!searchResults["Customer"]?.results?.length && (
                              <>
                                <CustomerResult customers={searchResults["Customer"].results} />
                                {renderLoadMore("Customer")}
                              </>
                            )}
                          </Box>
                        </CustomTabPanel>

                        <CustomTabPanel activeTab={activeTab} value="Shipment">
                          {!!searchResults["Shipment"]?.results?.length && (
                            <>
                              <ShipmentResult shipments={searchResults["Shipment"].results} />
                              {renderLoadMore("Shipment")}
                            </>
                          )}
                        </CustomTabPanel>

                        <CustomTabPanel activeTab={activeTab} value="Vendor">
                          {!!searchResults["Vendor"]?.results?.length && (
                            <>
                              <VendorResult vendors={searchResults["Vendor"].results} />
                              {renderLoadMore("Vendor")}
                            </>
                          )}
                        </CustomTabPanel>

                        <CustomTabPanel activeTab={activeTab} value="Customer">
                          {!!searchResults["Customer"]?.results?.length && (
                            <>
                              <CustomerResult customers={searchResults["Customer"].results} />
                              {renderLoadMore("Customer")}
                            </>
                          )}
                        </CustomTabPanel>
                      </>
                    ) : (
                      <div className="grid h-44 place-content-center text-center font-medium text-gray-500">
                        <p>Nothing matched with the keyword!</p>
                        <p>Try using another keyword.</p>
                      </div>
                    )
                  ) : (
                    <div className="grid h-44 place-content-center text-center font-medium text-gray-500">
                      <p>Your search results will appear here!</p>
                      <p>Type at least {MIN_SEARCH_LENGTH} characters to search!</p>
                    </div>
                  )}
                </Box>
              </Box>
            </>
          </Paper>
        </Container>
      </Backdrop>
    </>
  );
};

export default AdminSearchBar;